import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  bool,
  func,
  oneOf,
  string,
  element,
  object,
  instanceOf,
} from 'prop-types';
import { variant } from 'styled-system';
import {
  getDateFormat,
  ISO_DATE_FORMAT,
  isValidTime,
  formatTime,
  convertTimeFormat,
} from '@airasia-common/libraries/datetime';

import { CaptionText } from '../Text/Heading';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';

import Styled from '../../utils/Styled';
import { Variant } from '../../utils/constants';
import { defaultFunc } from '../../utils/props';
import Icon from './Icon';
import StyledInput from './StyledInput';

const FOCUSED = 'focused';
const MOVE = 'move';

const TextInputBox = Styled(Wrapper, {
  width: 1,
  flex: 1,
  position: 'relative',
});

const TextInputWrapper = styled(Row)(
  variant({
    variants: {
      [FOCUSED]: {
        borderColor: 'secondary',
      },
      [Variant.Error]: {
        borderColor: 'tertiaryOrange',
      },
      [MOVE]: {
        borderColor: '#023C40',
      },
    },
  })
);

TextInputWrapper.defaultProps = {
  position: 'relative',
  width: 1,
  border: '1px solid',
  borderColor: 'greyLight',
  borderRadius: 4,
  flex: 1,
  alignItems: 'center',
};

TextInputWrapper.displayName = 'TextInputWrapper';

const ActiveLabel = styled(CaptionText)(
  {
    lineHeight: '14px',
    opacity: 0.5,
    whiteSpace: 'nowrap',
  },
  variant({
    variants: {
      [Variant.Error]: {
        color: 'tertiaryOrange',
        opacity: 1,
      },
    },
  })
);

const ErrorHelperText = Styled(CaptionText, {
  color: 'tertiaryOrange',
  marginTop: 2,
});

ErrorHelperText.displayName = 'ErrorHelperText';

const InputMaskChildrenWrapper = ({ children, ...restProps }) =>
  React.cloneElement(children, restProps);

const isDateType = inputType => inputType === 'date';

const getInitDateValue = (propValue, dateFormat, defaultValue = '') => {
  return propValue
    ? convertTimeFormat(propValue, dateFormat, ISO_DATE_FORMAT)
    : defaultValue;
};

const validPattern = (value, pattern) => new RegExp(pattern, 'g').test(value);

const TextInput = ({
  label,
  placeholder: placeholderProp,
  errorMsg,
  onChange,
  onFocus,
  onBlur,
  variant,
  value: propValue,
  customIcon,
  children,
  boxProps,
  innerBoxProps,
  pattern,
  dateFormat,
  disabled,
  inputProps: nativeInputProps = {},
  autoFocus,
  showLabel,
  selectTextOnFocus = false,
  className,
  hideIcon,
  defaultSelect = '',
  onClick,
  move,
  ...restProps
}) => {
  const error = variant === Variant.Error;
  const isDateInput = isDateType(nativeInputProps.type || 'text');
  const initValue = isDateInput
    ? getInitDateValue(propValue, dateFormat, defaultSelect)
    : propValue;
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState(initValue);

  useEffect(() => {
    setValue(initValue);
    if (initValue && dateFormat && isValidTime(initValue, ISO_DATE_FORMAT)) {
      onChange(formatTime(new Date(initValue), dateFormat));
    }
    // eslint-disable-next-line
  }, [initValue]);

  const handleTextChange = event => {
    const { value } = event.target;

    if (value && pattern && !validPattern(value, pattern)) {
      return;
    }

    if (!isDateInput) {
      setValue(value);
      onChange(value, event);
      return;
    }

    if (dateFormat && isValidTime(value, ISO_DATE_FORMAT)) {
      setValue(value);
      onChange(formatTime(new Date(value), dateFormat));
    }
  };

  const handleClick = e => {
    if (onClick) onClick(e);
  };

  const handleBlur = event => {
    setFocused(false);
    onBlur(value, event);
  };

  const handelFocus = event => {
    if (selectTextOnFocus) {
      event.target.select();
    }
    setFocused(true);
    onFocus(event);
  };

  const inputProps = {
    placeholder: focused ? '' : placeholderProp,
    onFocus: handelFocus,
    onBlur: handleBlur,
    value: value,
    onChange: handleTextChange,
    onClick: handleClick,
    disabled: disabled,
    ...nativeInputProps,
  };

  const actived = showLabel || focused || value;

  return (
    <TextInputBox
      {...boxProps}
      backgroundColor={disabled ? 'greyLighter' : 'transparent'}
    >
      <TextInputWrapper
        className={className}
        variant={!error && focused ? (move ? MOVE : FOCUSED) : variant}
      >
        <Wrapper
          width={1}
          paddingX={16}
          paddingTop={actived ? '6px' : 14}
          paddingBottom={actived ? '8px' : 14}
          paddingRight={customIcon || variant !== Variant.Default ? 48 : 16}
          {...innerBoxProps}
        >
          {actived && (
            <ActiveLabel className="label" variant={variant}>
              {label}
            </ActiveLabel>
          )}
          {children ? (
            <InputMaskChildrenWrapper
              {...restProps}
              {...inputProps}
              focused={focused}
            >
              {children}
            </InputMaskChildrenWrapper>
          ) : (
            <StyledInput {...inputProps} autoFocus={autoFocus} />
          )}
        </Wrapper>
        {!hideIcon && (
          <Icon
            customIcon={customIcon}
            focused={focused}
            variant={
              variant === Variant.Success && !actived
                ? Variant.Default
                : variant
            }
          />
        )}
      </TextInputWrapper>
      {error && !!errorMsg && (
        <ErrorHelperText className="error">{errorMsg}</ErrorHelperText>
      )}
    </TextInputBox>
  );
};

TextInput.propTypes = {
  label: string,
  placeholder: string,
  errorMsg: string,
  onChange: func,
  onBlur: func,
  onFocus: func,
  onClick: func,
  value: string,
  touched: bool,
  customIcon: func,
  children: element,
  boxProps: object,
  innerBoxProps: object,
  pattern: instanceOf(RegExp),
  variant: oneOf([Variant.Success, Variant.Error, Variant.Default]),
  inputProps: object,
  type: string,
  dateFormat: string,
  autoFocus: bool,
  showLabel: bool,
  disabled: bool,
  selectTextOnFocus: bool,
  hideIcon: bool,
  className: string,
  defaultSelect: string,
  move: bool,
};

TextInput.defaultProps = {
  onChange: defaultFunc,
  onBlur: defaultFunc,
  onFocus: defaultFunc,
  onClick: defaultFunc,
  variant: Variant.Default,
  value: '',
  text: 'text',
  inputProps: {},
  dateFormat: getDateFormat(),
  autoFocus: false,
  hideIcon: false,
  defaultSelect: null,
};

export default TextInput;
